import { db } from './codexDB';
import { buildSeed } from '@/data/seed';
import { PROJECT_ID } from '@/lib/constants';
import { runDataMigrations } from './migrations';
import { seedPortraits } from './seedPortraits';
import { seedCities } from './seedCities';
import { seedAtlas } from './seedAtlas';
import type { Entity, Link } from '@/types/entity';
import type { Project } from '@/types/project';

export interface LoadedState {
  project: Project;
  entities: Entity[];
  links: Link[];
}

/** 빈 DB에 바이블 v3 시드 적재 (= 캐논 초기화 경로) */
export async function seedDatabase(): Promise<void> {
  const { project, entities, links } = buildSeed();
  await db.transaction('rw', db.entities, db.links, db.project, async () => {
    await Promise.all([db.entities.clear(), db.links.clear()]);
    await db.entities.bulkPut(entities);
    await db.links.bulkPut(links);
    await db.project.put(project);
  });
}

/** 부팅: 프로젝트 없으면 시드, 있으면 마이그레이션 후 로드 */
export async function loadOrSeed(): Promise<LoadedState> {
  let project = await db.project.get(PROJECT_ID);
  if (!project) {
    await seedDatabase();
    project = await db.project.get(PROJECT_ID);
  }
  project = await runDataMigrations(project!);
  void seedPortraits().catch(() => {}); // 핵심 초상 적재(비차단·멱등) — assets는 live로 반영
  void seedCities().catch(() => {}); // 도시 아트 적재(비차단·멱등) — assets만 만짐
  await seedAtlas().catch(() => {}); // 아틀라스 노드 meta.atlas+궤도 자산 — **엔티티 meta 만지므로 hydrate 전 await**
  const [entities, links] = await Promise.all([db.entities.toArray(), db.links.toArray()]);
  return { project, entities, links };
}

export async function loadAll(): Promise<LoadedState> {
  const [project, entities, links] = await Promise.all([
    db.project.get(PROJECT_ID),
    db.entities.toArray(),
    db.links.toArray(),
  ]);
  if (!project) throw new Error('프로젝트가 없습니다');
  return { project, entities, links };
}

export async function wipeAll(): Promise<void> {
  await db.transaction('rw', db.entities, db.links, db.project, async () => {
    await Promise.all([db.entities.clear(), db.links.clear(), db.project.clear()]);
  });
}
